import { roots } from './hydrate'

const unmountNode = (domNode: Element): void => {
  const root = roots.get(domNode)
  if (!root) return

  try {
    root.unmount()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    console.error(`Caught error calling root.unmount() for domNode`, domNode, e)
  } finally {
    // Drop the reference regardless of whether unmount() succeeded — reusing
    // a root that failed to unmount cleanly via hydrateComponent's
    // existingRoot.render() would throw "Cannot update an unmounted root".
    roots.delete(domNode)
  }
}

const unmount = (el: Element): void => {
  const componentId = el.getAttribute('data-component-id') || ''
  const domNode = document.getElementById(componentId)
  if (domNode === null) return

  unmountNode(domNode)
}

export const unmountReactComponents = (): void => {
  const elements = document.getElementsByClassName('vite-rails-react-component')
  Array.from(elements).forEach(unmount)
}

// turbo:before-frame-render bubbles from the <turbo-frame> that is about to
// have its children replaced, so event.target scopes the cleanup to that one
// frame — the roots elsewhere on the page (the side menu itself, chrome
// outside the frame) are still live and must not be torn down.
//
// Unlike unmountReactComponents this walks the roots Map instead of querying
// the document for .vite-rails-react-component elements. Every server render
// mints a fresh data-component-id (React::Renderer#component_id), so the
// incoming markup never matches the outgoing root's node: once Turbo has
// swapped the frame's children there is no element left to query, and the
// departed root — a mobx-react-lite observer of the global store — would
// stay mounted and re-render on every store mutation for the life of the
// tab. The Map already holds the node references, so unmounting and
// deleting from it here both releases the roots and keeps the Map from
// growing with each navigation.
export const unmountReactComponentsInFrame = (event: Event): void => {
  const frame = event.target
  if (!(frame instanceof Element)) return

  Array.from(roots.keys()).forEach((node) => {
    if (frame.contains(node)) unmountNode(node)
  })
}
